import { useTranslate } from "ra-core";

import { findDealLabel } from "../deals/dealUtils";
import { formatMxn } from "../incentives/IncentiveBars";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { PipelineStageTotal } from "./pipelineTotals";

/**
 * Almikia Epic E7 (TASK-094), generalized to any number of stages by Epic EP
 * (TASK-107): the Dirección panel's "Embudo · totales por etapa" -- one row
 * per EFFECTIVE stage (DATA CONTRACT EP (8): the 10 system stages plus any
 * direccion-created one, in configured order, an archived custom stage only
 * while it still holds leads) with its open-lead count and MXN total. Pure
 * presentation -- `DireccionPanel.tsx` owns the data fetching,
 * `pipelineTotals.ts` the calculation; `total.stage`'s label already comes
 * from `dealStages` (`useConfigurationContext()`), which is the effective
 * list itself, so no change was needed here to show a configured label.
 */

export interface PipelineTotalsProps {
  totals: PipelineStageTotal[];
  /**
   * True when the MXN totals come from a capped read that did not return
   * every RLS-visible open lead (`DireccionPanel.tsx`'s
   * `isPipelineAmountPartial`) -- `total.count` itself stays exact
   * regardless (a separate, real count query per stage).
   */
  isAmountPartial: boolean;
}

export function PipelineTotals({
  totals,
  isAmountPartial,
}: PipelineTotalsProps) {
  const translate = useTranslate();
  const { dealStages } = useConfigurationContext();

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xl font-semibold text-muted-foreground">
        {translate("crm.dashboard.direccion.pipeline_title")}
      </h2>
      <div className="flex flex-col gap-2">
        {totals.map((total) => (
          <div
            key={total.stage}
            className="flex items-center justify-between gap-3 rounded-2xl bg-card p-3"
          >
            <span className="text-base font-bold">
              {findDealLabel(dealStages, total.stage)}
            </span>
            <span className="shrink-0 text-base tabular-nums text-muted-foreground">
              {translate("crm.dashboard.direccion.pipeline_row", {
                smart_count: total.count,
                amount: formatMxn(total.amountMxn),
              })}
            </span>
          </div>
        ))}
      </div>
      {isAmountPartial ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.dashboard.direccion.partial_data_note")}
        </p>
      ) : null}
    </section>
  );
}
